<script setup lang="ts">
interface Props {
  title: string
  titleHighlight?: string
  subtitle: string
  buttonText?: string
  buttonLink?: string
  infoText?: string
}

withDefaults(defineProps<Props>(), {
  buttonText: 'Candidater maintenant',
  buttonLink: '/candidature',
  infoText: 'Candidature gratuite et sans engagement'
})

const { elementRef: sectionRef, isVisible: isVisible } = useScrollReveal()
</script>

<template>
  <section ref="sectionRef" class="py-section-py text-foreground relative border-y border-oxo-border transition-colors duration-300">
    <div class="container mx-auto px-container-px relative z-10 text-center">
      <div class="max-w-5xl mx-auto flex flex-col items-center">
        
        <!-- Animated Badge -->
        <div 
          class="inline-block px-4 sm:px-5 py-2 rounded-full bg-oxo-card-bg/20 border border-oxo-border text-foreground dark:text-oxo-yellow text-[10px] sm:text-xs font-black tracking-[0.2em] sm:tracking-[0.3em] uppercase mb-8 sm:mb-10 shadow-xl transition-[opacity,transform] duration-1000 delay-300"
          :class="isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'"
        >
          Prêt pour le prochain niveau ?
        </div>

        <!-- Main Title -->
        <h2 
          class="font-display font-black text-3xl sm:text-5xl md:text-7xl lg:text-8xl text-foreground mb-8 uppercase leading-[1.1] transition-[opacity,transform] duration-1000 delay-500 wrap-break-word overflow-hidden"
          :class="isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'"
        >
          {{ title }} <br class="hidden sm:block" />
          <span v-if="titleHighlight" class="text-transparent bg-clip-text bg-linear-to-r from-oxo-red to-oxo-yellow relative inline-block">
            {{ titleHighlight }}
          </span>
        </h2>

        <!-- Subtitle -->
        <p 
          class="text-foreground-dim text-lg md:text-2xl max-w-2xl mx-auto mb-12 sm:mb-16 font-light leading-relaxed transition-[opacity,transform] duration-1000 delay-700"
          :class="isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'"
        >
          {{ subtitle }}
        </p>

        <!-- CTA Button Box -->
        <div 
          class="flex flex-col items-center gap-6 transition-[opacity,transform] duration-1000 delay-900"
          :class="isVisible ? 'opacity-100 scale-100 translate-y-0' : 'opacity-0 scale-95 translate-y-10'"
        >
          <div class="relative group">
            
            <AppButton 
              :to="buttonLink" 
              variant="primary" 
              size="lg"
              class="relative z-10 transform transition-all duration-500 group-hover:scale-105 group-hover:-translate-y-1"
              data-umami-event="Global CTA - Click Candidater"
            >
              <span class="flex items-center gap-3">
                {{ buttonText }}
                <i class="fas fa-arrow-right text-lg group-hover:translate-x-2 transition-transform duration-300" aria-hidden="true"></i>
              </span>
            </AppButton>
          </div>

          <!-- Info Text -->
          <div v-if="infoText" class="flex items-center justify-center gap-2 text-foreground-dim text-[10px] sm:text-sm font-medium tracking-wide uppercase px-4 text-center">
            <span class="shrink-0 w-1.5 h-1.5 rounded-full bg-oxo-red animate-pulse"></span>
            <span class="wrap-break-word">{{ infoText }}</span>
          </div>

          <!-- Extra Content Slot -->
          <slot />
        </div>
      </div>
    </div>

    <!-- Simple Clean Background -->
    <!-- Simple Clean Background -->
  </section>
</template>
